import React, { useMemo, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { Stepper } from "primereact/stepper";
import { StepperPanel } from "primereact/stepperpanel";
import { Col, Row } from "react-bootstrap";
import { isEmpty } from "lodash";
import Location from "../../../../components/Common/Display/Location";
import { request } from "../../../../utils/Request";
import { Field, Form, Formik } from "formik";
import * as yup from "yup";
import { InputSingleSelectField } from "../../../../components/Shared/InputSingleSelectField";
import InputDateTime from "../../../../components/Shared/InputDateTime";
import { InputTextAreaField } from "../../../../components/Shared/InputTextAreaField";
import { InputField } from "../../../../components/Shared/InputField";
import { dateToUtcDate, getUserTimezone } from "../../../../utils/Helper";
import moment from "moment";
import VinWithType from "../../../../components/Common/Display/VinWithType";
import GroupedLoadsByRoute from "../../loads/partials/AlpOutboundLoadGroup/GroupedLoadsByRoute";
import Tooltip from "../../../../components/Common/Display/Tooltip";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import HasAccess from "../../../../utils/HasAccess";
import { getProviderApiPrefix } from "../../../../utils/providerApi";

type Props = {
    t?: any;
    toastify?: any;
    visible: boolean;
    selectedOrders: any[];
    sourceType?: 'emil_frey' | 'vehicle_logistics';
    onClose: any;
    onRemoveOrder: (orderId: any) => void;
    onClearAll: () => void;
};

function AvisoOutboundLoadForm(props: Props) {
    const { t, toastify, visible, onClose, selectedOrders, onRemoveOrder, onClearAll, sourceType } = props;
    const stepperRef = useRef<any>(null);
    const [step, setStep] = useState<number>(0); // 0 = confirm, 1 = validation, 2 = accept
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [creating, setCreating] = useState<boolean>(false);
    const [acceptSubmitting, setAcceptSubmitting] = useState<boolean>(false);

    const [validationRows, setValidationRows] = useState<any[]>([]);
    const [validationIsPartial, setValidationIsPartial] = useState<boolean>(false); // true when backend returns only failing orders

    const [groupToken, setGroupToken] = useState<string | null>(null);
    const [createdLoadsByOrderId, setCreatedLoadsByOrderId] = useState<any>({});

    const [acceptDropdowns, setAcceptDropdowns] = useState<any>(null);
    const [acceptLoads, setAcceptLoads] = useState<any[]>([]);
    const [acceptLoadsLoading, setAcceptLoadsLoading] = useState<boolean>(false);

    const resetLocalState = () => {
        setStep(0);
        setSubmitting(false);
        setCreating(false);
        setAcceptSubmitting(false);
        setValidationRows([]);
        setValidationIsPartial(false);
        setGroupToken(null);
        setCreatedLoadsByOrderId({});
        setAcceptDropdowns(null);
        setAcceptLoads([]);
        setAcceptLoadsLoading(false);
    };

    // When the drawer closes, reset everything so next open starts fresh.
    // Parent is responsible to refresh the list (it already does).
    useEffect(() => {
        if ( !visible ) {
            resetLocalState();
            onClearAll && onClearAll();
        }
    }, [visible]);

    const onHide = () => {
        resetLocalState();
        onClearAll && onClearAll();
        onClose && onClose();
    };

    const ordersForDisplay = useMemo(() => {
        return ( selectedOrders || [] )
            .filter((row: any) => {
                const normalized = row?.source_type === 'vehicle_logistics' ? 'vehicle_logistics' : 'emil_frey';
                if ( sourceType ) {
                    return normalized === sourceType;
                }
                return true;
            })
            .map((row: any) => {
            const journey = row.current_journey || {};
            const orderGuid = row.id || row.guid; // send local order guid; backend resolves source_id
            return {
                // Use row_id as the stable UI key (allows two rows for same order).
                id: row.row_id ?? row.id,
                order_guid: orderGuid,
                vin_number: row.vin_number,
                source_type: row.source_type,
                priorisation: row.priorisation,
                built_to_order_cd: row.built_to_order_cd,
                supplier_compound: row.emil_frey_supplier_compound,
                dealer: row.emil_frey_dealer,
                from_location: journey.from_location,
                to_location: journey.to_location,
            };
        });
    }, [selectedOrders, sourceType]);

    const providerApiPrefix = useMemo(() => {
        if ( sourceType ) {
            return getProviderApiPrefix(sourceType);
        }
        const itemSourceType = selectedOrders?.[0]?.source_type || null;
        return getProviderApiPrefix(itemSourceType);
    }, [selectedOrders, sourceType]);

    const validationByGuid = useMemo(() => {
        const map: any = {};
        ( validationRows || [] ).forEach((row: any) => {
            if ( row && row.id ) {
                map[row.id] = row;
            }
        });
        return map;
    }, [validationRows]);

    const goNext = () => {
        setStep((s: number) => Math.min(s + 1, 2));
    };

    const goBack = () => {
        setStep((s: number) => Math.max(s - 1, 0));
    };

    const goToValidationStep = () => {
        setStep(1);
    };

    const goToAcceptStep = () => {
        setStep(2);
    };

    const canCreateLoads = useMemo(() => {
        if ( isEmpty(ordersForDisplay) ) {
            return false;
        }

        // All orders must be OK to proceed to create.
        return ordersForDisplay.every((order: any) => {
            const row = validationByGuid[order.order_guid];
            const errors = row && Array.isArray(row.errors)
                ? row.errors
                : ( validationIsPartial ? [] : [t("validation data missing")] );

            return errors.length === 0;
        });
    }, [ordersForDisplay, validationByGuid, validationIsPartial, t]);

    // Step 2 After validation
    const onCreateLoads = async () => {
        if ( creating || submitting ) {
            return;
        }

        setCreating(true);
        setValidationRows([]);
        setValidationIsPartial(false);
        setGroupToken(null);
        setCreatedLoadsByOrderId({});

        const uniqueSourceTypes = Array.from(new Set(
            ordersForDisplay.map((item: any) => item.source_type || 'emil_frey')
        ));
        if ( uniqueSourceTypes.length > 1 ) {
            toastify && toastify(t("mixed provider selection is not allowed"), "error");
            return;
        }

        try {
            const response = await request({
                url: `${ providerApiPrefix }/aviso/outbound/loads/store`,
                method: "POST",
                data: {
                    orders: ordersForDisplay.map((o: any) => o.order_guid),
                    create: true,
                }
            });

            const data = response?.data?.data ?? response?.data;
            const token = data?.group_token ?? null;
            const loads = Array.isArray(data?.loads) ? data.loads : [];

            const map: any = {};
            loads.forEach((row: any) => {
                if ( row && row.order_id ) {
                    map[row.order_id] = row.load_number;
                }
            });

            setGroupToken(token);
            setCreatedLoadsByOrderId(map);
            // If no validation errors, backend created loads -> go straight to accept step.
            goToAcceptStep();
        } catch (e: any) {
            if ( e && e.status === 422 ) {
                toastify && toastify(t("unprocessable entity"), "error");

                // Preferred backend format:
                // { message: "...", errors: { orders: [ { id: "<guid>", errors: ["..."] } ] } }
                const orders = e?.data?.errors?.orders ?? e?.data?.orders;
                if ( Array.isArray(orders) ) {
                    const byId: any = {};
                    orders.forEach((r: any) => {
                        if ( r && r.id ) {
                            byId[r.id] = r;
                        }
                    });

                    // Expand to full list so UI shows all orders and "create" stays disabled.
                    const expanded = ordersForDisplay.map((o: any) => {
                        const key = o.order_guid;
                        const row = byId[key];
                        return {
                            id: key,
                            errors: row && Array.isArray(row.errors) ? row.errors : [],
                        };
                    });

                    setValidationRows(expanded);
                    setValidationIsPartial(false);

                    // Move to validation screen so user can see issues.
                    goToValidationStep();
                    return;
                }

                // Fallback: Laravel validation shapes
                const errorsObject = e?.data?.errors || {};
                const rowsFromKey = Object.keys(errorsObject).map((key: string) => {
                    const parts = key.split("orders.");
                    const id = parts.length === 2 ? parts[1] : key;
                    return { id, errors: errorsObject[key] };
                });

                if ( rowsFromKey.length > 0 ) {
                    setValidationRows(rowsFromKey);
                    setValidationIsPartial(false);
                    goToValidationStep();
                    return;
                }

                return;
            }
            toastify && toastify(t("server error"), "error");
        } finally {
            setCreating(false);
        }
    };

    const loadAcceptDropdowns = async () => {
        try {
            const response = await request({
                url: `${ providerApiPrefix }/outbound/loads/create`,
                params: { type: "list" },
            });
            setAcceptDropdowns(response?.data?.data ?? response?.data);
        } catch (e: any) {
            setAcceptDropdowns({ drivers: [], transportVehicles: [], subContractors: [] });
        }
    };

    useEffect(() => {
        if ( visible && step === 2 && !acceptDropdowns ) {
            loadAcceptDropdowns();
        }
    }, [visible, step]);

    const fetchAcceptLoads = async (token: string) => {
        if ( acceptLoadsLoading || isEmpty(token) ) {
            return;
        }

        setAcceptLoadsLoading(true);
        try {
            const response = await request({
                url: `${ providerApiPrefix }/aviso/outbound/groups/${ token }`,
            });
            const data = response?.data?.data ?? response?.data;
            setAcceptLoads(Array.isArray(data?.alp_outbound_loads) ? data.alp_outbound_loads : []);
        } catch (e: any) {
            setAcceptLoads([]);
        } finally {
            setAcceptLoadsLoading(false);
        }
    };

    useEffect(() => {
        // In accept step we need the REAL loads so user can unlink before accepting.
        if ( visible && step === 2 && !isEmpty(groupToken) ) {
            fetchAcceptLoads(groupToken as string);
        }
        if ( step !== 2 ) {
            setAcceptLoads([]);
        }
    }, [visible, step, groupToken]);

    const canRemoveLoad = (load: any): boolean => {
        const status = `${ load?.status ?? '' }`.toLowerCase();
        const hasGateOut = !isEmpty(load?.gate_out_confirmed_at);
        return (status === 'announced' || status === 'accepted') && !hasGateOut;
    };

    const onRemoveLoadFromGroup = async (loadGuid: string) => {
        if ( acceptSubmitting || isEmpty(groupToken) || isEmpty(loadGuid) ) {
            return;
        }

        const getApiErrorMessage = (e: any): string | null => {
            const errors = e?.data?.errors;
            if ( errors && typeof errors === 'object' ) {
                const messages: string[] = [];
                Object.keys(errors).forEach((key: string) => {
                    const items = (errors as any)[key];
                    if ( Array.isArray(items) ) {
                        items.forEach((m: any) => {
                            if ( m ) {
                                messages.push(String(m));
                            }
                        });
                    }
                });
                if ( messages.length > 0 ) {
                    return messages.join(', ');
                }
            }
            return e?.data?.message ? String(e.data.message) : null;
        };

        try {
            await request({
                method: 'POST',
                url: `${ providerApiPrefix }/aviso/outbound/loads/${ groupToken }/remove`,
                data: {
                    load_ids: [loadGuid],
                },
            });
            toastify && toastify(t("record updated", { item: t('outbound load') }), "info");
            await fetchAcceptLoads(groupToken as string);
        } catch (e: any) {
            if ( e && e.status === 422 ) {
                const msg = getApiErrorMessage(e);
                toastify && toastify(msg || t("unprocessable entity"), "error");
            } else if ( e && e.status === 404 ) {
                toastify && toastify(t("action not allowed: 404"), "error");
            } else {
                toastify && toastify(t("server error"), "error");
            }
        }
    };

    type TAcceptForm = {
        driver_id?: any;
        transport_vehicle_id?: any;
        sub_contractor_id?: any;
        estimated_loading_time?: any;
        estimated_unloading_time?: any;
        comments?: any;
        cmr?: any;
    };

    const acceptValidationSchema = (t: any) => yup.object().shape({
        sub_contractor_id: yup.string().nullable(),
        driver_id: yup.string().required(t('the field is required.', { field: t('driver') })),
        transport_vehicle_id: yup.string().required(t('the field is required.', { field: t('transport vehicle') })),
        estimated_loading_time: yup.date(t("the field value is not valid", { field: t('estimated loading time') }))
        .required(t('the field is required.', { field: t('estimated loading time') })).nullable(),
        estimated_unloading_time: yup.date(t("the field value is not valid", { field: t('estimated unloading time') }))
        .min(yup.ref('estimated_loading_time'), t('the field must be a date after', {
            field: "estimated unloading time",
            fieldAfter: "estimated loading time"
        }))
        .required(t('the field is required.', { field: t('estimated unloading time') })).nullable(),
        comments: yup.string().nullable(),
        cmr: yup.string().nullable(),
    });

    const onAcceptGroup = async (payloads: any, { setErrors }: any) => {
        if ( !groupToken ) {
            toastify && toastify(t("server error"), "error");
            return;
        }

        const cleanGuid = (val: any) => {
            let v = val;
            if ( v && typeof v === 'object' && ( v as any ).id ) {
                v = ( v as any ).id;
            }
            if ( typeof v !== 'string' ) {
                return v;
            }
            const trimmed = v.trim();
            // backend dropdown sometimes returns quoted strings like "\"GUID\""
            if (
                ( trimmed.startsWith('"') && trimmed.endsWith('"') ) ||
                ( trimmed.startsWith("'") && trimmed.endsWith("'") )
            ) {
                return trimmed.substring(1, trimmed.length - 1);
            }
            return trimmed;
        };

        setAcceptSubmitting(true);
        try {
            await request({
                method: "POST",
                url: `${ providerApiPrefix }/aviso/outbound/loads/${ groupToken }/accept`,
                data: {
                    ...payloads,
                    group_token: groupToken,
                    driver_id: cleanGuid(payloads?.driver_id),
                    transport_vehicle_id: cleanGuid(payloads?.transport_vehicle_id),
                    estimated_unloading_time: ( payloads.estimated_unloading_time
                        ? dateToUtcDate(payloads.estimated_unloading_time, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss')
                        : null ),
                    estimated_loading_time: ( payloads.estimated_loading_time
                        ? dateToUtcDate(payloads.estimated_loading_time, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss')
                        : null ),
                }
            });

            toastify && toastify(t("load(s) added"), "info");
            onClearAll && onClearAll();
            onClose && onClose();
        } catch (e: any) {
            if ( e && e.status === 422 ) {
                // show toast like step 2 so user knows it's validation
                toastify && toastify(t("unprocessable entity"), "error");

                const apiErrors = e?.data?.errors || {};
                // If backend returns group_token error (e.g. "No announced loads found..."),
                // display it under driver_id field so it is visible in the form.
                if ( apiErrors && apiErrors.group_token && !apiErrors.driver_id ) {
                    setErrors({
                        ...apiErrors,
                        driver_id: apiErrors.group_token,
                    });
                    return;
                }

                setErrors(apiErrors);
                return;
            }
            toastify && toastify(t("server error"), "error");
        } finally {
            setAcceptSubmitting(false);
        }
    };

    const header = (
        <div className="bg-body-tertiary card-header">
            <h5 className={ "card-title mt-0" }>{ t("create outbound load") }</h5>
            <p>
                { step === 0 ? t("step 1: confirm orders") : ( step === 1 ? t("step 2: validation") : t("step 3: accept loads") ) }
                { sourceType && (
                    <span className="ms-2 text-capitalize">({ sourceType === 'vehicle_logistics' ? t('vehicle logistics') : t('emil frey') })</span>
                ) }
            </p>
        </div>
    );

    return (
        <Sidebar
            header={ header }
            visible={ visible }
            position="right"
            onHide={ onHide }
            blockScroll={ true }
            style={ { width: '86vw', maxWidth: '1400px' } }
            closeOnEscape={ false }
        >
            <ConfirmPopup />
            <div style={ {
                height: '3px',
                background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
            } }></div>

            <div className="mt-3">
                <Stepper
                    ref={ stepperRef }
                    linear
                    activeStep={ step }
                >
                    <StepperPanel header={ ') ' + t("order selection") }>
                        <div className="mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div>
                                    <strong>{ t("selected orders") }</strong>
                                    <span className="ms-2 text-muted">({ ordersForDisplay.length })</span>
                                </div>
                                <div>
                                    <Button
                                        type="button"
                                        size="small"
                                        severity="danger"
                                        outlined
                                        disabled={ ordersForDisplay.length <= 0 }
                                        onClick={ onClearAll }
                                        tooltip={ t("remove all selected orders") }
                                        tooltipOptions={ { position: "top" } }
                                    >
                                        <i className="pi pi-trash me-2" />
                                        { t("clear") }
                                    </Button>
                                </div>
                            </div>

                            {
                                isEmpty(ordersForDisplay) &&
                              <div className="p-3 bg-body-tertiary rounded">
                                  { t("no orders selected") }
                              </div>
                            }

                            {
                                !isEmpty(ordersForDisplay) &&
                              <div className="list-group">
                                  { ordersForDisplay.map((row: any, index: number) => (
                                      <div
                                          key={ row.id ?? index }
                                          className="list-group-item list-group-item-action border rounded-3 mb-2 p-0 shadow-sm"
                                          style={ { overflow: 'hidden', backgroundColor: '#f8fafc' } }
                                      >
                                          {/* Header */ }
                                          <div
                                              className="d-flex justify-content-between align-items-start p-2 bg-body-tertiary border-bottom">
                                              <div className="w-100">
                                                  <div className="d-flex justify-content-between align-items-start">
                                                      <div className="me-2 w-100">
                                                          <div
                                                              className="d-flex justify-content-between align-items-center">
                                                              <div className="text-muted"
                                                                   style={ { fontSize: '12px' } }>
                                                                  { t("order") } #{ index + 1 }
                                                              </div>
                                                              <Button
                                                                  type="button"
                                                                  size="small"
                                                                  severity="danger"
                                                                  text
                                                                  onClick={ () => onRemoveOrder(row.id) }
                                                                  tooltip={ t("remove") }
                                                                  tooltipOptions={ { position: "top" } }
                                                              >
                                                                  <i className="pi pi-times" />
                                                              </Button>
                                                          </div>
                                                      </div>
                                                  </div>
                                              </div>
                                          </div>

                                          {/* Body */ }
                                          <div className="p-2 border-start border-4 border-primary">
                                              <Row className="g-2">
                                                  {/* Left side: order info */ }
                                                  <Col sm={ 12 } md={ 5 }>
                                                      <div className="small">
                                                          <div className="mb-1">
                                                              <strong>{ t("vin number") }:</strong>{ " " }
                                                              <span>
                                                                    <VinWithType vinNumber={ row.vin_number || "" } />
                                                                </span>
                                                          </div>
                                                          <div className="mb-1">
                                                              <strong>{ t("supplier compound") }:</strong>{ " " }
                                                              <span
                                                                  className="text-capitalize">{ row.supplier_compound || "-" }</span>
                                                          </div>
                                                          <div className="mb-1">
                                                              <strong>{ t("dealer") }:</strong>{ " " }
                                                              <span
                                                                  className="text-capitalize">{ row.dealer || "-" }</span>
                                                          </div>
                                                          <div className="mb-1">
                                                              <strong>{ t("validation") }:</strong>{ " " }
                                                              <span
                                                                  className="text-muted">{ t("not checked yet") }</span>
                                                          </div>
                                                      </div>
                                                  </Col>

                                                  {/* Right side: locations */ }
                                                  <Col sm={ 12 } md={ 7 }>
                                                      <Row className="g-2">
                                                          <Col sm={ 12 } md={ 6 }>
                                                              <div className="text-muted"
                                                                   style={ { fontSize: '12px' } }>
                                                                  <strong>{ t("from location") }</strong>
                                                              </div>
                                                              <div>
                                                                  { row.from_location
                                                                      ? <Location item={ row.from_location }
                                                                                  titled={ 0 } />
                                                                      : "-"
                                                                  }
                                                              </div>
                                                          </Col>
                                                          <Col sm={ 12 } md={ 6 }>
                                                              <div className="text-muted"
                                                                   style={ { fontSize: '12px' } }>
                                                                  <strong>{ t("to location") }</strong>
                                                              </div>
                                                              <div>
                                                                  { row.to_location
                                                                      ?
                                                                      <Location item={ row.to_location } titled={ 0 } />
                                                                      : "-"
                                                                  }
                                                              </div>
                                                          </Col>
                                                      </Row>
                                                  </Col>
                                              </Row>
                                          </div>
                                      </div>
                                  )) }
                              </div>
                            }

                            {/* Step 1 only: next steps will be added after confirmation */ }
                            <Row className="mt-3">
                                <Col sm={ 12 } className="text-end">
                                    <Button
                                        type="button"
                                        size="small"
                                        disabled={ creating || isEmpty(ordersForDisplay) }
                                        onClick={ onCreateLoads }
                                        tooltip={ t("check validation") }
                                        tooltipOptions={ { position: "top" } }
                                    >
                                        <i className={ `pi ${ creating ? 'pi-spin pi-spinner' : 'pi-arrow-right' } me-2` } />
                                        { t("create load") }
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    </StepperPanel>

                    <StepperPanel header={ ') ' + t("create load") }>
                        <div className="mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div>
                                    <strong>{ t("validation") }</strong>
                                    <span className="ms-2 text-muted">({ validationRows.length })</span>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        type="button"
                                        size="small"
                                        severity="secondary"
                                        outlined
                                        onClick={ goBack }
                                    >
                                        <i className="pi pi-arrow-left me-2" />
                                        { t("back") }
                                    </Button>
                                </div>
                            </div>

                            {
                                isEmpty(validationRows) &&
                              <div className="p-3 bg-body-tertiary rounded">
                                  { t("no data available") }
                              </div>
                            }

                            {
                                !isEmpty(validationRows) &&
                              <div className="list-group">
                                  { ordersForDisplay.map((order: any, index: number) => {
                                      // Validation response may use either stable row id or local order guid.
                                      const row =
                                          validationByGuid[order.id] ||
                                          validationByGuid[order.order_guid];

                                      // If backend returned partial errors (422), missing row means OK.
                                      // If backend returned full list (200), missing row likely means id mismatch.
                                      const errors = row && Array.isArray(row.errors)
                                          ? row.errors
                                          : ( validationIsPartial ? [] : [t("validation data missing")] );

                                      const ok = errors.length === 0;

                                      return (
                                          <div
                                              key={ order.id ?? index }
                                              className="list-group-item border rounded-3 mb-2 p-2 shadow-sm"
                                              style={ { backgroundColor: ok ? '#f0fdf4' : '#fef2f2' } }
                                          >
                                              <Row className="g-2">
                                                  {/* 1) Info */ }
                                                  <Col xs={ 12 } sm={ 4 }>
                                                      <div className="small">
                                                          <div className="mb-1">
                                                              <strong>{ t("vin number") }:</strong>{ " " }
                                                              <span>{ order.vin_number || "-" }</span>
                                                          </div>
                                                          <div className="mb-1">
                                                              <strong>{ t("supplier compound") }:</strong>{ " " }
                                                              <span
                                                                  className="text-capitalize">{ order.supplier_compound || "-" }</span>
                                                          </div>
                                                          <div className="mb-1">
                                                              <strong>{ t("dealer") }:</strong>{ " " }
                                                              <span
                                                                  className="text-capitalize">{ order.dealer || "-" }</span>
                                                          </div>
                                                      </div>
                                                  </Col>

                                                  {/* 2) Locations */ }
                                                  <Col xs={ 12 } sm={ 4 }>
                                                      <div className="mb-2">
                                                          <div className="text-muted" style={ { fontSize: '12px' } }>
                                                              <strong>{ t("from location") }</strong>
                                                          </div>
                                                          <div>
                                                              { order.from_location
                                                                  ?
                                                                  <Location item={ order.from_location } titled={ 1 } />
                                                                  : "-"
                                                              }
                                                          </div>
                                                      </div>

                                                      <div>
                                                          <div className="text-muted" style={ { fontSize: '12px' } }>
                                                              <strong>{ t("to location") }</strong>
                                                          </div>
                                                          <div>
                                                              { order.to_location
                                                                  ? <Location item={ order.to_location } titled={ 1 } />
                                                                  : "-"
                                                              }
                                                          </div>
                                                      </div>
                                                  </Col>

                                                  {/* 3) Errors / Ready */ }
                                                  <Col xs={ 12 } sm={ 4 }>
                                                      {
                                                          ok
                                                              ? <div className="text-success small">
                                                                  <strong>{ t("ready to add in load") }</strong></div>
                                                              : (
                                                                  <div className="small">
                                                                      <div className="text-danger mb-1">
                                                                          <strong>{ t("errors") }</strong></div>
                                                                      <ul className="mb-0">
                                                                          { errors.map((msg: string, i: number) => (
                                                                              <li key={ i }>{ msg }</li>
                                                                          )) }
                                                                      </ul>
                                                                  </div>
                                                              )
                                                      }
                                                  </Col>
                                              </Row>
                                          </div>
                                      );
                                  }) }
                              </div>
                            }

                            <Row className="mt-3">
                                <Col sm={ 12 } className="text-end">
                                    <Button
                                        type="button"
                                        size="small"
                                        disabled={ creating || !canCreateLoads }
                                        onClick={ onCreateLoads }
                                        tooltip={ t("create load") }
                                        tooltipOptions={ { position: "top" } }
                                    >
                                        <i className={ `pi ${ creating ? 'pi-spin pi-spinner' : 'pi-arrow-right' } me-2` } />
                                        { t("create load") }
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    </StepperPanel>

                    <StepperPanel header={ ') ' + t("accept loads") }>
                        <div className="mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div>
                                    <strong>{ t("accept loads") }</strong>
                                    <span className="ms-2 text-muted">({ ordersForDisplay.length })</span>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        type="button"
                                        size="small"
                                        severity="secondary"
                                        outlined
                                        onClick={ goBack }
                                        disabled={ acceptSubmitting }
                                    >
                                        <i className="pi pi-arrow-left me-2" />
                                        { t("back") }
                                    </Button>
                                </div>
                            </div>

                            <div className="mb-3">
                                { acceptLoadsLoading && (
                                    <div className="text-muted small">{ t("loading") }...</div>
                                ) }

                                { !acceptLoadsLoading && (
                                    <GroupedLoadsByRoute
                                        loads={ acceptLoads }
                                        t={ t }
                                        // If there is only 1 load, hide delete button (user should delete the whole group).
                                        showActionColumn={ Array.isArray(acceptLoads) && acceptLoads.length > 1 }
                                        renderActionCell={ (row: any) => {
                                            if ( !Array.isArray(acceptLoads) || acceptLoads.length <= 1 ) {
                                                return null;
                                            }
                                            const allowed = canRemoveLoad(row);
                                            const id = `${ row?.id ?? '' }`;
                                            return (
                                                <HasAccess hasAnyAccess={ ['alpgrouploadsupdate'] }>
                                                    <Tooltip tooltip={ t('delete item') } position={ "top" }>
                                                        <a
                                                            title={ t('delete item') }
                                                            className={ `cursor-pointer ms-2 btn btn-soft-danger waves-effect waves-light btn-xs ${allowed ? '' : 'disabled'}` }
                                                            onClick={ (event: any) => {
                                                                if ( !allowed || isEmpty(id) ) {
                                                                    return;
                                                                }
                                                                confirmPopup({
                                                                    target: event.currentTarget,
                                                                    message: t('do you want to delete this record?'),
                                                                    icon: 'pi pi-info-circle',
                                                                    // @ts-ignore
                                                                    defaultFocus: 'reject',
                                                                    acceptClassName: 'p-button-danger',
                                                                    accept: () => onRemoveLoadFromGroup(id),
                                                                    reject: () => {},
                                                                });
                                                            } }
                                                        >
                                                            <i className="fas fa-trash-alt" />
                                                        </a>
                                                    </Tooltip>
                                                </HasAccess>
                                            );
                                        } }
                                    />
                                ) }
                            </div>

                            <Formik
                                enableReinitialize
                                initialValues={ {
                                    driver_id: null,
                                    sub_contractor_id: null,
                                    transport_vehicle_id: null,
                                    estimated_loading_time: null,
                                    estimated_unloading_time: null,
                                    comments: null,
                                    cmr: null,
                                } }
                                validationSchema={ acceptValidationSchema(t) }
                                onSubmit={ onAcceptGroup }
                            >
                                { (formikProps) => (
                                    <Form onSubmit={ formikProps.handleSubmit }>
                                        <Row className="g-2">
                                            <Col sm={ 12 } md={ 6 }>
                                                <Field
                                                    component={ InputSingleSelectField }
                                                    name="sub_contractor_id"
                                                    options={ ( acceptDropdowns?.subContractors || [] ) }
                                                    label={ t('sub contractor') }
                                                    placeholder={ t('select') + ' ' + t('sub contractor') }
                                                    formText={ t('leave empty to see only direct drivers/vehicles. select subcontractor to filter.') }
                                                    onChange={ () => {
                                                        formikProps.setFieldValue('driver_id', null);
                                                        formikProps.setFieldValue('transport_vehicle_id', null);
                                                    } }
                                                />
                                            </Col>
                                            <Col sm={ 12 } md={ 6 }></Col>
                                            <Col sm={ 12 } md={ 6 }>
                                                <Field
                                                    component={ InputSingleSelectField }
                                                    name="driver_id"
                                                    trackBy="id"
                                                    options={
                                                        ( acceptDropdowns?.drivers || [] )
                                                        .filter((item: any) => (
                                                            isEmpty(formikProps.values.sub_contractor_id)
                                                                ? isEmpty(item?.sub_contractor_id)
                                                                : item.sub_contractor_id === formikProps.values.sub_contractor_id
                                                        ))
                                                        .filter((item: any) => item && item.is_active !== false && item.is_active !== 'false')
                                                    }
                                                    label={ t("driver") }
                                                    placeholder={ t("select") + " " + t("driver") }
                                                    isRequired
                                                />
                                            </Col>
                                            <Col sm={ 12 } md={ 6 }>
                                                <Field
                                                    component={ InputSingleSelectField }
                                                    panelClassName={ "hide-p-toggler" }
                                                    name="transport_vehicle_id"
                                                    trackBy="id"
                                                    optionLabel="label"
                                                    filterBy="label,truck_plate_number,trailer_plate_number"
                                                    options={
                                                        ( acceptDropdowns?.transportVehicles || [] )
                                                        .map((item: any) => {
                                                            return {
                                                                ...item,
                                                                icon: ( !isEmpty(item.active_journey) ? 'fas fa-circle text-yellow-500' : 'fas fa-circle text-green-500' ),
                                                            };
                                                        })
                                                        .filter((item: any) => (
                                                            isEmpty(formikProps.values.sub_contractor_id)
                                                                ? isEmpty(item?.sub_contractor_id)
                                                                : item.sub_contractor_id === formikProps.values.sub_contractor_id
                                                        ))
                                                        .filter((item: any) => item && item.is_active !== false && item.is_active !== 'false')
                                                    }
                                                    label={ t("transport vehicle") }
                                                    placeholder={ t("select") + " " + t("transport vehicle") }
                                                    isRequired
                                                />
                                            </Col>

                                            <Col sm={ 12 } md={ 6 }>
                                                <Field
                                                    placeholder={ t("pick a date") }
                                                    component={ InputDateTime }
                                                    name="estimated_loading_time"
                                                    type="datetime"
                                                    label={ t("estimated loading time") }
                                                    minDate={ moment().tz(getUserTimezone()).toDate() }
                                                    isRequired
                                                />
                                            </Col>
                                            <Col sm={ 12 } md={ 6 }>
                                                <Field
                                                    placeholder={ t("pick a date") }
                                                    component={ InputDateTime }
                                                    name="estimated_unloading_time"
                                                    type="datetime"
                                                    label={ t("estimated unloading time") }
                                                    minDate={ moment().tz(getUserTimezone()).toDate() }
                                                    isRequired
                                                />
                                            </Col>

                                            <Col sm={ 12 } md={ 6 }>
                                                <Field
                                                    component={ InputField }
                                                    name="cmr"
                                                    label={ t("cmr") }
                                                    placeholder={ t("enter") + " " + t("cmr").toLowerCase() }
                                                />
                                            </Col>

                                            <Col sm={ 12 }>
                                                <Field
                                                    component={ InputTextAreaField }
                                                    name="comments"
                                                    label={ t("comments") }
                                                    placeholder={ t("enter") + " " + t("comments").toLowerCase() }
                                                />
                                            </Col>

                                            <Col sm={ 12 } className="text-end mt-2">
                                                <Button
                                                    type="submit"
                                                    size="small"
                                                    disabled={ acceptSubmitting || !groupToken || (Array.isArray(acceptLoads) && acceptLoads.length <= 0) }
                                                >
                                                    <i className={ `pi ${ acceptSubmitting ? 'pi-spin pi-spinner' : 'pi-check' } me-2` } />
                                                    { t("accept load") }
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Form>
                                ) }
                            </Formik>
                        </div>
                    </StepperPanel>
                </Stepper>
                <ConfirmPopup />
            </div>
        </Sidebar>
    );
}

AvisoOutboundLoadForm.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(AvisoOutboundLoadForm);

